import React, { FC, memo, useEffect, useMemo, useState } from 'react'

import {
    FirmwareGeneralModel,
    useFirmwareDeleteFirmwareMutation,
    useFirmwareGetActiveFirmwareQuery,
    useFirmwareGetFirmwaresByFilterQuery,
    useFirmwareSetActiveFirmwareMutation,
} from '../../common/redux/api/web-api'
import useText from '../../common/hooks/useText'
import { ActivateFirmwareModal } from '../../lib/activate-firmware-modal'
import ConfirmationModal from '../ConfirmationModal'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import { Product } from '../../support/pages/firmware-page/FirmwarePage'

import FirmwareList from './firmwareList'

export type SelectFactoryFirmwareTabProps = {
    product: Partial<Product>
}

export const DEFAULT_VISIBLE_ITEMS = 3
const maxItemsCount = 1000

const SelectFactoryFirmwareTab: FC<SelectFactoryFirmwareTabProps> = ({ product }) => {
    const text = useText()
    const [visibleItemsCount, setVisibleItemsCount] = useState(DEFAULT_VISIBLE_ITEMS)
    const [activateFirmware] = useFirmwareSetActiveFirmwareMutation()
    const [deleteFirmware] = useFirmwareDeleteFirmwareMutation()
    const { data: firmware } = useFirmwareGetFirmwaresByFilterQuery(
        {
            productId: product.productId,
            firmwareType: product.firmwareType,
            noOfItems: visibleItemsCount,
            ocppEnabled: product.ocppEnabledFirmware,
        },
        { skip: !product.productId }
    )

    const [availableFirmwares, setAvailableFirmwares] = useState<FirmwareGeneralModel[]>([])
    const [confirmRemoveFirmware, setConfirmRemoveFirmware] = useState(false)
    const [selectedFirmwareId, setSelectedFirmwareId] = useState<number | null>(null)
    const [confirmActivateFirmware, setConfirmActivateFirmware] = useState(false)

    useEffect(() => setAvailableFirmwares(firmware?.items || []), [firmware])
    const totalAvailableFirmwares = useMemo(() => firmware?.totalCount || 0, [firmware?.totalCount])

    const { data: activeFirmwareData, isLoading: activeFirmwareIsLoading } = useFirmwareGetActiveFirmwareQuery(
        {
            productId: product.productId,
        },
        { skip: !product || !product.productId }
    )

    const firmwareList = useMemo(() => {
        return activeFirmwareData &&
            activeFirmwareData.firmwareType === product.firmwareType &&
            !product.ocppEnabledFirmware
            ? [
                  {
                      ...activeFirmwareData,
                      isActive: true,
                  },
                  ...availableFirmwares,
              ].filter((obj, index, arr) => {
                  return index === arr.findIndex((o) => obj.id === o.id)
              })
            : availableFirmwares
    }, [availableFirmwares, activeFirmwareData, product])

    const onRemoveFirmware = (id: number | null, removeIsConfirmed: boolean) => {
        setSelectedFirmwareId(id)
        setConfirmRemoveFirmware(removeIsConfirmed)
    }

    const onActivateFirmware = (id: number | null, activateIsConfirmed: boolean) => {
        setSelectedFirmwareId(id)
        setConfirmActivateFirmware(activateIsConfirmed)
    }

    const handleRemoveConfirm = () => {
        if (product.productId && selectedFirmwareId) {
            deleteFirmware({ productId: product.productId, firmwareId: selectedFirmwareId }).then(
                () => {
                    onRemoveFirmware(null, false)
                    notifySuccess(text('msgFirmwareRemoved'))
                },
                ({ error }) => error && notifyError(error)
            )
        }
    }

    const handleActivateConfirm = () => {
        activateFirmware({
            productId: product.productId,
            firmwareId: selectedFirmwareId,
        }).then(
            () => {
                onActivateFirmware(selectedFirmwareId, false)
                notifySuccess(text('msgFirmwareActivated'))
            },
            ({ error }) => error && notifyError(error)
        )
    }

    const shouldShowLess = availableFirmwares.length > DEFAULT_VISIBLE_ITEMS
    const shouldShowMore = !shouldShowLess && totalAvailableFirmwares > DEFAULT_VISIBLE_ITEMS

    return (
        <div className="flex">
            <div className="w-12">
                <FirmwareList
                    title={text('lblSelectFactoryFirmware')}
                    showMoreIsVisible={shouldShowMore}
                    onShowMore={() => setVisibleItemsCount(maxItemsCount)}
                    showLessIsVisible={shouldShowLess}
                    onShowLess={() => setAvailableFirmwares(availableFirmwares.slice(0, DEFAULT_VISIBLE_ITEMS))}
                    onRemove={(id) => {
                        onRemoveFirmware(id, true)
                    }}
                    onActivate={(id) => onActivateFirmware(id, true)}
                    loading={activeFirmwareIsLoading}
                    isOcpp={product.ocppEnabledFirmware ?? false}
                    data={firmwareList}
                />
            </div>
            {confirmRemoveFirmware && (
                <ConfirmationModal
                    message={text('msgConfirmFwRemoval')}
                    onClose={() => onRemoveFirmware(null, false)}
                    confirmButtonText={text('lblConfirm')}
                    onConfirm={handleRemoveConfirm}
                    title={text('lblConfirmFirmwareRemove')}
                />
            )}

            {confirmActivateFirmware ? (
                <ActivateFirmwareModal
                    onCancel={() => onActivateFirmware(null, false)}
                    onConfirm={handleActivateConfirm}
                    currentFactoryFirmware={availableFirmwares.find(
                        (firmware) => firmware.id === activeFirmwareData?.id
                    )}
                    newFactoryFirmware={availableFirmwares.find((firmware) => firmware.id === selectedFirmwareId)}
                />
            ) : null}
        </div>
    )
}

export default memo(SelectFactoryFirmwareTab)
